import {ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {FITHParsedGameData} from "../../server/data/FITHParsedGameData";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {FITHGameModuleConfig} from "../../FITHGameModuleConfig";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {FITHGameConfig} from "../../FITHGameConfig";
import {FITHGameAssets} from "../../FITHGameAssets";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {FITHSoundManager} from "../../sound/FITHSoundManager";

export class FITHFreeSpinIntroView extends ScreenView {
    private _animation: PIXI.spine.Spine;
    private _pages: PIXI.spine.Spine[] = [];
    private _numPages: number = 8;
    private _tl: TimelineLite | null;
    private _blackGfx: PIXI.Graphics;
    private _dummyDOverlay: PIXI.Sprite[][] = [];
    private _deadBreak: TimelineSprite[][] = [];
    private _wildExplodeEff: TimelineSprite[][] = [];
    private _activeRowsFS: number;
    private readonly _texts: string[] = ["Reset to 3 spins", "Collect coin value", "Multiply all coins on reel with value", "Open up stone area and/or bottom chest(s)", "Collects all coins on reel to one position", "Collects all coins on all reels to one position", "Collects all coins on all reel for each spin", "Collects all coins on reel for each spin"];

    private static actuallyDoTransition(): void {
        FITHGameModuleConfig.background.toggleFSOverlay(false, 0);
        FITHGameModuleConfig.spinController.resetBonusReelsToSpin();
        FITHGameModuleConfig.reelAreaView.enterFS();
        FITHGameModuleConfig.anticipationView.stopAnticipation();
        FITHGameModuleConfig.wildMultiplierView.hideMultiplierItem();
    }

    protected initAnimations(): void {
        this._animation = new PIXI.spine.Spine(GameResources.getSpineAsset("FS_Intro"));
        this._animation.visible = false;

        super.initAnimations();

        const textStyle: PIXI.TextStyle = new PIXI.TextStyle({
            fill: "#fff",
            fontFamily: "Arial",
            fontSize: 30,
            fontWeight: FontWeight.BOLD,
            wordWrap: true,
            wordWrapWidth: 680,
            breakWords: true,
            align: "center"
        });
        let page: PIXI.spine.Spine,
            textItem: PIXI.Text;
        for (let i: number = 0; i < this._numPages; i++) {
            page = new PIXI.spine.Spine(GameResources.getSpineAsset("FS_Info_" + (i + 1)));
            page.visible = false;

            textItem = new PIXI.Text(SlotGame.api.translations.translate(this._texts[i]), textStyle);
            textItem.anchor.set(0.5);
            textItem.y = 70;
            page.addChild(textItem);

            this._pages.push(page);
        }

        this.createTransitionEffectItems();

        this._blackGfx = new PIXI.Graphics();
        this._blackGfx.visible = false;
        this.addChild(this._blackGfx);
    }

    private createTransitionEffectItems(): void {
        const numRows: number = 2,
            numItems: number = 6;

        let deadItem: PIXI.Sprite,
            deadBreak: TimelineSprite,
            wildExplodeEff: TimelineSprite;

        for (let i: number = 0; i < numRows; i++) {
            this._dummyDOverlay.push([]);
            this._deadBreak.push([]);
            this._wildExplodeEff.push([]);
            for (let j: number = 0; j < numItems; j++) {
                deadItem = new PIXI.Sprite(FITHGameAssets.getSymbolStateTextures("D2")[0]);
                deadItem.visible = false;
                deadItem.anchor.set(0.5);
                this._dummyDOverlay[i].push(deadItem);

                deadBreak = new TimelineSprite(GameResources.getTextures("deadExplode"));
                deadBreak.hide();
                deadBreak.anchor.set(0.5);
                this._deadBreak[i].push(deadBreak);

                wildExplodeEff = new TimelineSprite(GameResources.getTextures("explodeEff"));
                wildExplodeEff.hide();
                wildExplodeEff.anchor.set(0.5);
                wildExplodeEff.scale.set(0.4);
                wildExplodeEff.blendMode = PIXI.BLEND_MODES.SCREEN;
                this._wildExplodeEff[i].push(wildExplodeEff);

                this.addChild(deadItem, deadBreak, wildExplodeEff);
            }
        }
    }

    protected startPresentation(data?: FITHParsedGameData, onShowComplete?: () => void): void {
        FITHGameModuleConfig.background.toggleFSOverlay(true, 0.4);

        this.removeChild(this._button);
        for (let i = 0; i < this._numPages; i++) {
            this._pages[i].visible = false;
            this._pages[i].state.clearTracks();
            this.removeChild(this._pages[i]);
        }
        super.startPresentation(data, onShowComplete);

        this._animation.position.set(NolimitApplication.screenBounds.width / 2, NolimitApplication.screenBounds.height / 2);
        this.addChildAt(this._animation, 0);
        this._animation.updateTransform();
        this._animation.visible = true;

        this.alpha = 1;
        this._activeRowsFS = data ? data.activeRowsOpenedEnteringBonus : 0;

        const tl: TimelineLite = new TimelineLite(),
            spineTween: SpineTween = new SpineTween(this._animation, 0, "base");

        // #227
        FITHSoundManager.playFSIntroWagonIn();
        tl.add(() => FITHSoundManager.playFSIntroText(), 1.3);
        tl.add(() => FITHSoundManager.playFSIntroWagonOut(), 2.8);

        tl.add(() => this.onIntroAnimationCompleted(), spineTween.totalDuration());
    }

    protected stopPresentation(closeCallback?: () => void): void {
        const timeline: TimelineLite = new TimelineLite({
            onComplete: () => super.stopPresentation(closeCallback)
        });
        timeline.add([
            () => this.turnOffItems(), // #152 tried to do this action via below method but somehow the stage scale transition wasn't smooth, don't know why.
            () => this.fadeOutBlack(),
            () => {
                // #319
                FITHSoundManager.playContinueButton();

                // #235
                FITHSoundManager.switchMainToFS();
            }
        ], 0);
        timeline.add(this.doExplodeEffectRowWise(), 1);

        // #224, This will turn off Autoplay if in restricted jurisdiction
        if(SlotGame.autoPlay.turnOffAutoplayOnBonus) {
            SlotGame.autoPlay.turnOffAutoplayOnBonus();
        }
    }

    // #245
    protected setButtonPosition(data: IResizeData): void {
        super.setButtonPosition(data);

        if (NolimitApplication.isLandscape) {
            this._button.setSize(384, 102, 1);
            this._button.position.set(this._screenWidth * 0.5, this._screenHeight * 0.8);
        } else {
            this._button.setSize(307, 82, 0.8);
            this._button.position.set(this._screenWidth * 0.5, this._screenHeight * 0.88);
        }
    }

    protected onResize(resizeData: IResizeData): void {
        super.onResize(resizeData);
        const screenBounds: ScreenBounds = NolimitApplication.screenBounds,
            halfWidth: number = screenBounds.width / 2,
            halfHeight: number = screenBounds.height / 2;

        this.position.set(screenBounds.left, screenBounds.top);

        this._animation.position.set(halfWidth, halfHeight);
        for (let i = 0; i < this._numPages; i++) {
            this._pages[i].position.set(halfWidth, halfHeight + 60);
        }

        this.resizeItems(screenBounds);
    }

    private resizeItems(screenBounds: ScreenBounds): void {
        const middleRows: number = 2,
            numItems: number = 6,
            gameConfigInstance: FITHGameConfig = FITHGameConfig.instance,
            baseX: number = 75,
            symWidthBonusGame: number = gameConfigInstance.SYMBOL_WIDTH_BONUS_GAME,
            baseY: number = 198,
            symHeightBonusGame: number = gameConfigInstance.SYMBOL_HEIGHT_BONUS_GAME;
        let deadItem: PIXI.Sprite,
            deadBreak: TimelineSprite,
            wildExplodeEff: TimelineSprite,
            xPos: number,
            yPos: number,
            xOffset: number = 0,
            yOffset: number = 0;

        // #233, to negate screen dimensions' effect
        if (NolimitApplication.isLandscape) {
            xOffset = -(screenBounds.left);
        } else {
            yOffset = -(screenBounds.top);
        }

        for (let i: number = 0; i < middleRows; i++) {
            yPos = baseY + yOffset + ((i + 3) * symHeightBonusGame); // #182, + 3 is because we are dealing with 3rd last and 2nd last rows out of all 6 rows in bonus game.
            for (let j: number = 0; j < numItems; j++) {
                xPos = baseX + xOffset + (j * symWidthBonusGame);

                deadItem = this._dummyDOverlay[i][j];
                deadItem.position.set(xPos, yPos);

                deadBreak = this._deadBreak[i][j];
                deadBreak.position.set(xPos, yPos);

                wildExplodeEff = this._wildExplodeEff[i][j];
                wildExplodeEff.position.set(xPos, yPos);
            }
        }

        this._blackGfx.clear();
        this._blackGfx.beginFill(0x000000);
        this._blackGfx.drawRect(0, 0, this._screenWidth, this._screenHeight);
        this._blackGfx.endFill();
    }

    private onIntroAnimationCompleted(): void {
        this._tl = new TimelineLite({
            onComplete: () => this.onIntroAnimationCompleted()
        });

        for (let i: number = 0; i < this._numPages; i++) {
            this._tl.add([
                () => {
                    this.addChild(this._pages[i]);
                    this._pages[i].state.clearTracks();
                    this._pages[i].skeleton.setSlotsToSetupPose();
                    this._pages[i].state.setEmptyAnimation(0, 0);
                    this._pages[i].updateTransform();
                    this._pages[i].visible = true;
                    this._button.visible = true;
                    this.addChild(this._button);
                },
                new SpineTween(this._pages[i], 0, "base", 0)
            ]);
            this._tl.add(() => {
                this.removeChild(this._pages[i]);
                this._pages[i].visible = false;
                this._pages[i].state.clearTracks();
            });
        }
    }

    private turnOffItems(): void {
        this._animation.visible = false;
        this._animation.state.clearTracks();
        this.removeChild(this._animation);

        for (let i = 0; i < this._numPages; i++) {
            this._pages[i].visible = false;
            this._pages[i].state.clearTracks();
            this.removeChild(this._pages[i]);
        }

        if (this._tl) {
            this._tl.pause();
            this._tl.kill();
            this._tl = null;
        }
        this._button.visible = false;
    }

    private fadeOutBlack(): void {
        const numItems: number = 6,
            stage: PIXI.Container = StageManager.stage,
            stWd4: number = stage.width / 4,
            stHt4: number = stage.height / 4;

        for (let i: number = 0; i < this._activeRowsFS; i++) {
            for (let j: number = 0; j < numItems; j++) {
                this._dummyDOverlay[i][j].visible = true;
            }
        }
        this._blackGfx.visible = true;
        this._blackGfx.alpha = 1;
        TweenLite.to(this._blackGfx, 0.5, {alpha: 0, ease: Linear.easeNone});

        stage.pivot.set(stWd4, stHt4);
        stage.position.set(stWd4, stHt4);
        stage.scale.set(1.5);
        TweenLite.to(stage.scale, 0.9, {x: 1, y: 1, onComplete: () => {
                stage.pivot.set(0);
                stage.position.set(0);

                this._blackGfx.visible = false;
            }
        });

        FITHFreeSpinIntroView.actuallyDoTransition();
    }

    private doExplodeEffectRowWise(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            numItems: number = 6;

        for (let i: number = 0; i < this._activeRowsFS; i++) {
            tl.add(() => {
                // #231
                FITHSoundManager.playFSRowExplode();

                for (let j: number = 0; j < numItems; j++) {
                    this._dummyDOverlay[i][j].visible = false;
                    this._deadBreak[i][j].getAnimationAutoShowHide(true, true);
                    this._wildExplodeEff[i][j].getAnimationAutoShowHide(true, true, [4]);
                }
            }, (i * 0.5));
        }
        // this is so that the last row animation is completed too.
        tl.add(() => {}, ((this._activeRowsFS) * 0.5));

        return tl;
    }
}